/**
 * shared/api/client — openapi-fetch client typed by the generated schema.
 *
 * The client is pre-configured with:
 *   - Base URL from shared/config/env (never hardcoded)
 *   - Auth middleware (token-attach + onUnauthorized hook points)
 *
 * Usage:
 *   import { apiClient } from '@/shared/api';
 *   const { data, error } = await apiClient.GET('/api/health/');
 *
 * The returned data/error types are inferred from schema.d.ts — no manual
 * type annotations required at call sites.
 *
 * DO NOT import this module from layers above shared via deep paths.
 * Always import through the shared/api public barrel (index.ts).
 */

import createClient from 'openapi-fetch';
import type { paths } from './schema';
import { API_URL } from '../config/env';
import { authMiddleware } from './middleware';

/**
 * The singleton openapi-fetch client typed by the backend OpenAPI schema.
 * All API calls go through this instance.
 */
export const apiClient = createClient<paths>({
  baseUrl: API_URL,
  // Send credentials so the httpOnly refresh-token cookie is stored on login
  // and sent on refresh — even cross-origin (the SPA runs on a different port
  // than the API). Without this, silent refresh fails and the user is logged
  // out on the first request that needs re-authentication.
  credentials: 'include',
});

// Register the auth middleware (token-attach + 401 hook point).
// Both are no-ops until Phase 3 injects the real token store.
apiClient.use(authMiddleware);
